import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Users, Star } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [userStatus, setUserStatus] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      };

      const eventsRes = await axios.get(`${API}/api/servers/temp/events`);
      const foundEvent = eventsRes.data.find(e => e.event_id === eventId);
      setEvent(foundEvent);

      const attendeesRes = await axios.get(`${API}/api/events/${eventId}/attendees`);
      setAttendees(attendeesRes.data);

      const reviewsRes = await axios.get(`${API}/api/events/${eventId}/reviews`);
      setReviews(reviewsRes.data);

      if (user) {
        const userAttendee = attendeesRes.data.find(a => a.user_id === user.user_id);
        setUserStatus(userAttendee?.status);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (status) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/events/${eventId}/rsvp?status=${status}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      toast.success('RSVP updated!');
      fetchEvent();
    } catch (error) {
      console.error('Error updating RSVP:', error);
      toast.error('Failed to update RSVP');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/events/${eventId}/reviews`, {
        rating,
        comment
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      toast.success('Review submitted!');
      setShowReview(false);
      fetchEvent();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    }
  };

  if (loading || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const isPastEvent = new Date(event.event_date) < new Date();

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b-4 border-black bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="mr-4 border-2 border-black rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Event Details</h1>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border-4 border-black rounded-3xl p-8 shadow-brutal-lg mb-8">
          <h2 className="text-4xl font-black text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>
            {event.title}
          </h2>
          <p className="text-lg text-slate-700 mb-6">{event.description}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-3 p-4 bg-cyan-100 border-2 border-black rounded-xl">
              <Calendar className="w-6 h-6" />
              <div>
                <div className="font-bold text-sm text-slate-600">Date & Time</div>
                <div className="font-semibold">{new Date(event.event_date).toLocaleString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-lime-100 border-2 border-black rounded-xl">
              <MapPin className="w-6 h-6" />
              <div>
                <div className="font-bold text-sm text-slate-600">Location</div>
                <div className="font-semibold">{event.location.town}, {event.location.state}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-pink-100 border-2 border-black rounded-xl">
              <Users className="w-6 h-6" />
              <div>
                <div className="font-bold text-sm text-slate-600">Attendees</div>
                <div className="font-semibold">{event.attendee_count} {event.max_attendees && `/ ${event.max_attendees}`}</div>
              </div>
            </div>
          </div>

          {!isPastEvent && (
            <div className="flex gap-3">
              <Button
                onClick={() => handleRSVP('going')}
                disabled={userStatus === 'going'}
                className="flex-1 bg-lime-400 hover:bg-lime-500 text-slate-900 border-2 border-black rounded-full font-bold shadow-brutal"
              >
                {userStatus === 'going' ? '✓ Going' : 'RSVP Going'}
              </Button>
              <Button
                onClick={() => handleRSVP('interested')}
                disabled={userStatus === 'interested'}
                variant="outline"
                className="flex-1 border-2 border-black rounded-full font-bold"
              >
                {userStatus === 'interested' ? '✓ Interested' : 'Maybe'}
              </Button>
            </div>
          )}

          {isPastEvent && userStatus === 'going' && !showReview && (
            <Button
              onClick={() => setShowReview(true)}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
            >
              Leave a Review
            </Button>
          )}

          {showReview && (
            <form onSubmit={handleSubmitReview} className="mt-6 p-6 bg-slate-50 border-2 border-black rounded-xl">
              <h3 className="font-black text-lg mb-4" style={{fontFamily: 'Outfit'}}>Write a Review</h3>
              <div className="mb-4">
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(r => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRating(r)}
                      className="text-3xl"
                    >
                      <Star className={`w-8 h-8 ${r <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your experience..."
                rows={4}
                className="mb-4 border-2 border-black rounded-xl"
              />
              <div className="flex gap-3">
                <Button type="submit" className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold">
                  Submit Review
                </Button>
                <Button type="button" onClick={() => setShowReview(false)} variant="outline" className="flex-1 border-2 border-black rounded-full font-bold">
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>

        <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal mb-8">
          <h3 className="text-2xl font-black text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>Attendees ({attendees.length})</h3>
          <div className="space-y-2">
            {attendees.map(attendee => (
              <div key={attendee.user_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <span className="font-semibold">{attendee.user_name}</span>
                <span className="text-sm px-3 py-1 bg-lime-200 border border-black rounded-full font-bold">
                  {attendee.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {reviews.length > 0 && (
          <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal">
            <h3 className="text-2xl font-black text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>Reviews ({reviews.length})</h3>
            <div className="space-y-4">
              {reviews.map(review => (
                <div key={review.review_id} className="p-4 bg-slate-50 border-2 border-black rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold">{review.user_name}</span>
                    <div className="flex">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <p className="text-slate-700">{review.comment}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}